<script setup lang="ts">
import animateBezier from '~/utils/animate'

interface TabPosition {
  scrollTo: number
  clientWidth: number
  offsetLeft: number
}

const props = defineProps<{
  modelValue?: string | number
  tabs: any[]
  stretch?: boolean
  quickNavigationLeft?: boolean
  quickNavigationRight?: boolean
}>()
const emit =
  defineEmits<(e: 'update:modelValue', value: number | string) => void>()

const showArrows = ref(true)
const disabledLeft = ref(false)
const disabledRight = ref(false)
const activeOffset = ref(0)
const activeWidth = ref(0)
const tabsNavigationWrapperRef = ref<any>(null)
const tabRefs = ref<any[]>([])

const active = computed(() =>
  props.tabs.find((item) => String(item.name) === String(props.modelValue)),
)
const activeIndicatorStyle = computed(() => ({
  width: `${activeWidth.value}px`,
  transform: `translateX(${activeOffset.value}px)`,
}))

const animate = (
  start: number,
  end: number,
  callback: (value: number) => void,
  finish?: () => void,
) => {
  const change = end - start

  animateBezier(
    (progress: number) => {
      callback(parseInt(String(progress * change + start), 10))
    },
    () => {
      callback(end)
      finish?.()
    },
    250,
  )
}
const checkDisabled = () => {
  if (!tabsNavigationWrapperRef.value) {
    return
  }

  const { scrollWidth, offsetWidth, scrollLeft } =
    tabsNavigationWrapperRef.value

  disabledLeft.value = scrollLeft === 0
  disabledRight.value = scrollWidth - scrollLeft <= offsetWidth
}
const animateScroll = (end: number) => {
  if (!tabsNavigationWrapperRef.value || !end) {
    return
  }

  animate(
    tabsNavigationWrapperRef.value.scrollLeft,
    end,
    (value: number) => {
      tabsNavigationWrapperRef.value.scrollLeft = value
    },
    () => {
      checkDisabled()
    },
  )
}
const animateTo = (tabPosition: TabPosition) => {
  if (!tabPosition) {
    return
  }

  const { scrollTo, clientWidth, offsetLeft } = tabPosition
  if (scrollTo !== undefined) {
    animateScroll(scrollTo)
  }

  animate(activeWidth.value, clientWidth, (value) => {
    activeWidth.value = value
  })
  animate(activeOffset.value, offsetLeft, (value) => {
    activeOffset.value = value
  })
}
const moveTo = ({ scrollTo, clientWidth, offsetLeft }: TabPosition) => {
  if (scrollTo !== undefined) {
    tabsNavigationWrapperRef.value.scrollLeft = scrollTo
  }

  activeWidth.value = clientWidth
  activeOffset.value = offsetLeft
}
const calculateActiveStyle = () => {
  if (!active.value) {
    return
  }

  const $el = tabRefs.value.find((refItem: any) => {
    return refItem?.id?.toString() === active.value?.name.toString()
  })

  if (!$el) {
    return
  }

  const { clientWidth, offsetLeft } = $el

  const wrapper = tabsNavigationWrapperRef.value

  const offsetWidth = clientWidth + offsetLeft
  const scrollViewport = wrapper.offsetWidth + wrapper.scrollLeft

  let scrollTo: number

  if (offsetWidth > scrollViewport) {
    scrollTo = wrapper.scrollLeft + offsetWidth - scrollViewport
  } else if (offsetLeft < wrapper.scrollLeft) {
    scrollTo = offsetLeft
  }

  return { scrollTo, clientWidth, offsetLeft }
}
const scrollToLeft = () => {
  if (disabledLeft.value) {
    return
  }

  const { offsetWidth, scrollLeft } = tabsNavigationWrapperRef.value
  const end = Math.max(scrollLeft - offsetWidth, 0)

  animateScroll(end)
}
const scrollToRight = () => {
  if (disabledRight.value) {
    return
  }

  const { scrollWidth, offsetWidth, scrollLeft } =
    tabsNavigationWrapperRef.value
  const distance = scrollWidth - Math.max(scrollLeft, offsetWidth)
  const end = scrollLeft + Math.min(distance, offsetWidth)

  animateScroll(end)
}
const click = (tab: any) => {
  if (active.value?.name === tab.name) {
    return
  }

  emit('update:modelValue', tab.name)
}

watch(active, () => {
  if (!active.value) {
    return
  }

  animateTo(calculateActiveStyle())
})

onMounted(() => {
  setTimeout(() => moveTo(calculateActiveStyle()), 20)
})
</script>

<template>
  <div class="flex overflow-hidden relative items-center">
    <div v-if="showArrows || !!$slots.prefix" class="flex items-center gap-x-1">
      <slot name="prefix" />
      <Button
        v-if="showArrows"
        :disabled="disabledLeft"
        variant="gray"
        class="px-1.5"
        @click="scrollToLeft"
      >
        <div class="i-heroicons-solid-chevron-left text-lg" />
      </Button>
    </div>
    <div
      ref="tabsNavigationWrapperRef"
      class="relative flex overflow-scroll flex-1 gap-x-1 items-center px-2 hide-scroll"
    >
      <div
        v-for="tab in tabs"
        :id="tab.name"
        :key="tab.name"
        ref="tabRefs"
        class="px-4 py-3 text-base font-semibold text-center whitespace-nowrap transition-all duration-150 ease-in-out cursor-pointer select-none z-10"
        :class="[
          String(tab.name) === String(modelValue)
            ? 'text-white'
            : 'text-gray-500 hover:text-gray-900',
          { 'flex-1': stretch },
        ]"
        @click="click(tab)"
      >
        <slot :tab="tab">
          <span v-html="tab.label" />
        </slot>
      </div>
      <div
        class="absolute left-0 bottom-1 h-10 transform bg-secondary-700 rounded-lg"
        :style="activeIndicatorStyle"
      />
    </div>
    <div v-if="showArrows || !!$slots.suffix" class="flex items-center gap-x-2">
      <Button
        v-if="showArrows"
        :disabled="disabledRight"
        variant="gray"
        class="px-1.5"
        @click="scrollToRight"
      >
        <div class="i-heroicons-solid-chevron-right text-lg" />
      </Button>
      <slot name="suffix" />
    </div>
  </div>
</template>
